import { snapdom } from '@zumer/snapdom';

/**
 * Screenshot capture.
 *
 * snapdom clones the DOM into an SVG foreignObject and rasterises it, which is
 * fast and needs no permission prompt — but it inherits three problems that have
 * to be handled here rather than in the library:
 *
 *  1. Cross-origin images taint the canvas and abort the export. Images we can
 *     route through our own proxy (`useProxy`); a <canvas> already painted with
 *     cross-origin content cannot be rescued and must be swapped for a placeholder.
 *  2. Cross-origin iframes render blank — a browser security boundary with no
 *     client-side fix. We label them so the reviewer knows it is a tool
 *     limitation and not the page being broken.
 *  3. Sensitive fields would otherwise be captured verbatim.
 *
 * Mutations are made to the live DOM and reverted in a `finally`, because
 * snapdom's clone step reads computed styles from the real elements.
 */

export interface CaptureOptions {
  /** Base URL of our service; used as snapdom's CORS fallback proxy. */
  apiBase: string;
  /** Root element to capture. Defaults to the whole document. */
  target?: HTMLElement;
  /** Selector for the widget's own UI, so it never appears in its own screenshot. */
  excludeSelectors?: string[];
}

export interface CaptureResult {
  dataUrl: string;
  width: number;
  height: number;
  /** Non-fatal problems worth telling the reporter about. */
  warnings: string[];
}

const PLACEHOLDER_ATTR = 'data-fb-placeholder';
const MAX_PIXELS = 4096 * 4096;

type Restore = () => void;

/**
 * A canvas is "tainted" once cross-origin content is drawn into it; reading it
 * throws a SecurityError, which would fail the whole capture. Detect by
 * attempting the read, and replace the offenders with a labelled box.
 */
function neutraliseTaintedCanvases(root: ParentNode): Restore[] {
  const restores: Restore[] = [];

  for (const canvas of root.querySelectorAll('canvas')) {
    let tainted = false;
    try {
      canvas.getContext('2d')?.getImageData(0, 0, 1, 1);
    } catch {
      tainted = true;
    }

    // WebGL contexts cannot be read back this way at all; treat them as tainted
    // so they get a placeholder rather than silently rendering blank.
    if (!tainted) {
      try {
        if (canvas.getContext('webgl') || canvas.getContext('webgl2')) tainted = true;
      } catch {
        tainted = true;
      }
    }

    if (!tainted) continue;

    const placeholder = document.createElement('div');
    placeholder.setAttribute(PLACEHOLDER_ATTR, 'canvas');
    const rect = canvas.getBoundingClientRect();
    placeholder.style.cssText = [
      `width:${rect.width || canvas.width}px`,
      `height:${rect.height || canvas.height}px`,
      'display:inline-flex',
      'align-items:center',
      'justify-content:center',
      'background:repeating-linear-gradient(45deg,#eee,#eee 8px,#e0e0e0 8px,#e0e0e0 16px)',
      'color:#555',
      'font:12px system-ui,sans-serif',
      'border:1px dashed #aaa',
      'box-sizing:border-box',
    ].join(';');
    placeholder.textContent = '⃠ canvas — not capturable';

    const parent = canvas.parentNode;
    if (!parent) continue;
    parent.insertBefore(placeholder, canvas);
    const previousDisplay = canvas.style.display;
    canvas.style.display = 'none';

    restores.push(() => {
      placeholder.remove();
      canvas.style.display = previousDisplay;
    });
  }

  return restores;
}

/** Cross-origin iframes always paint blank; make that legible in the screenshot. */
function labelCrossOriginIframes(root: ParentNode): { restores: Restore[]; count: number } {
  const restores: Restore[] = [];
  let count = 0;

  for (const frame of root.querySelectorAll('iframe')) {
    let sameOrigin = false;
    try {
      sameOrigin = Boolean(frame.contentDocument);
    } catch {
      sameOrigin = false;
    }
    if (sameOrigin) continue;

    count += 1;
    const rect = frame.getBoundingClientRect();
    const placeholder = document.createElement('div');
    placeholder.setAttribute(PLACEHOLDER_ATTR, 'iframe');
    placeholder.style.cssText = [
      `width:${rect.width || 300}px`,
      `height:${rect.height || 150}px`,
      'display:inline-flex',
      'align-items:center',
      'justify-content:center',
      'background:#f6f6f6',
      'color:#666',
      'font:12px system-ui,sans-serif',
      'border:1px dashed #bbb',
      'box-sizing:border-box',
      'text-align:center',
      'padding:8px',
    ].join(';');
    placeholder.textContent = '⃠ embedded frame — cannot be captured by the browser';

    const parent = frame.parentNode;
    if (!parent) continue;
    parent.insertBefore(placeholder, frame);
    const previousDisplay = frame.style.display;
    frame.style.display = 'none';

    restores.push(() => {
      placeholder.remove();
      frame.style.display = previousDisplay;
    });
  }

  return { restores, count };
}

/**
 * Masks values that should never leave the browser. Password fields always;
 * anything the host site tags with `data-feedback-mask` as well. Elements marked
 * `data-feedback-redact` are removed entirely by snapdom's `exclude` option.
 */
function maskSensitiveFields(root: ParentNode): Restore[] {
  const restores: Restore[] = [];
  const selector = 'input[type="password"], [data-feedback-mask]';

  for (const element of root.querySelectorAll<HTMLElement>(selector)) {
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      const previous = element.value;
      element.value = '•'.repeat(Math.min(previous.length, 12));
      restores.push(() => {
        element.value = previous;
      });
    } else {
      const previousFilter = element.style.filter;
      element.style.filter = 'blur(6px)';
      restores.push(() => {
        element.style.filter = previousFilter;
      });
    }
  }

  return restores;
}

/** Keeps the encoded PNG within the server's size limit on very large pages. */
function chooseScale(target: HTMLElement): number {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = target.scrollWidth || window.innerWidth;
  const height = target.scrollHeight || window.innerHeight;
  const pixels = width * height * dpr * dpr;
  if (pixels <= MAX_PIXELS) return dpr;
  return Math.max(1, dpr * Math.sqrt(MAX_PIXELS / pixels));
}

export async function captureScreenshot(options: CaptureOptions): Promise<CaptureResult> {
  const target = options.target ?? document.body;
  const warnings: string[] = [];
  const restores: Restore[] = [];

  try {
    restores.push(...neutraliseTaintedCanvases(document));

    const iframes = labelCrossOriginIframes(document);
    restores.push(...iframes.restores);
    if (iframes.count > 0) {
      warnings.push(
        `${iframes.count} embedded frame${iframes.count === 1 ? '' : 's'} could not be captured (browser restriction).`,
      );
    }

    restores.push(...maskSensitiveFields(document));

    const exclude = [
      ...(options.excludeSelectors ?? []),
      '[data-feedback-redact]',
      // Never capture ourselves.
      '[data-fb-widget]',
    ];

    const result = await snapdom(target, {
      exclude,
      excludeMode: 'remove',
      // Images blocked by CORS are retried through our own proxy, which adds the
      // Access-Control-Allow-Origin header the origin CDN omits.
      useProxy: `${options.apiBase}/api/v1/proxy?url=`,
      embedFonts: true,
      compress: true,
      scale: chooseScale(target),
      backgroundColor: '#ffffff',
      fast: true,
      /*
       * Measures the clone against the live DOM and pins diverging boxes.
       * It roughly doubles capture time, which is worth paying here: without it
       * text can re-wrap during rasterisation, so a report about a layout or
       * wrapping bug would arrive with a screenshot that does not match what the
       * reporter actually saw — the one thing this tool must get right.
       */
      reconcile: true,
    });

    const image = await result.toPng();

    return {
      dataUrl: image.src,
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      warnings,
    };
  } finally {
    // Reverse order, so nested mutations unwind cleanly.
    for (let i = restores.length - 1; i >= 0; i -= 1) {
      try {
        restores[i]?.();
      } catch {
        /* a failed restore must not mask the original error */
      }
    }
  }
}
